
import { useEffect, useRef, useState } from 'react';

const perfis = [
  {
    icon: 'ri-emotion-sad-line',
    titulo: 'Quem sente que a vida está no piloto automático',
    descricao:
      'Você acorda, trabalha, dorme e repete. Sente que os dias passam sem propósito e quer reencontrar o sentido da sua jornada.',
    destaque: 'Reconecte-se com seus sonhos',
  },
  {
    icon: 'ri-heart-add-line',
    titulo: 'Quem está passando por uma fase difícil',
    descricao:
      'Separação, luto, demissão ou crise existencial. Você precisa de uma âncora emocional para atravessar a tempestade e enxergar o futuro com esperança.',
    destaque: 'Encontre força na vulnerabilidade',
  },
  {
    icon: 'ri-rocket-2-line',
    titulo: 'Quem quer crescer e evoluir',
    descricao:
      'Você já está bem, mas sabe que pode ir além. Busca clareza sobre seus objetivos e quer criar um compromisso real com sua melhor versão.',
    destaque: 'Eleve seu potencial ao máximo',
  },
  {
    icon: 'ri-parent-line',
    titulo: 'Quem deseja deixar um legado',
    descricao:
      'Pais, mães, mentores e líderes que querem registrar seus valores, sonhos e mensagens para inspirar as próximas gerações.',
    destaque: 'Suas palavras transcendem o tempo',
  },
  {
    icon: 'ri-mental-health-line',
    titulo: 'Quem busca autoconhecimento profundo',
    descricao:
      'Você quer se entender melhor, identificar padrões limitantes e criar novos caminhos neurais através da escrita consciente e da visualização.',
    destaque: 'Descubra quem você realmente é',
  },
  {
    icon: 'ri-graduation-cap-line',
    titulo: 'Quem está em transição de carreira ou vida',
    descricao:
      'Mudança de profissão, aposentadoria, mudança de cidade. Momentos de transição pedem clareza, e a carta é o mapa para o próximo capítulo.',
    destaque: 'Navegue a mudança com propósito',
  },
];

const ParaQuemESection = () => {
  const [visibleCards, setVisibleCards] = useState<Set<number>>(new Set());
  const [headerVisible, setHeaderVisible] = useState(false);
  const cardsRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = entry.target.getAttribute('data-idx');
            if (idx !== null) {
              setVisibleCards((prev) => {
                const next = new Set(prev);
                next.add(Number(idx));
                return next;
              });
            }
            if (entry.target === headerRef.current) {
              setHeaderVisible(true);
            }
          }
        });
      },
      { threshold: 0.15 },
    );

    if (headerRef.current) observer.observe(headerRef.current);
    cardsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-luxury-black py-28 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent" />
      <div className="absolute top-32 left-0 w-80 h-80 bg-gold-500/3 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-gold-500/3 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-20 transition-all duration-1000 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-gold-500/40" />
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-500/60">
              Público-alvo
            </span>
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-gold-500/40" />
          </div>
          <h2 className="font-['Playfair_Display'] text-4xl lg:text-5xl text-white font-light mb-5">
            Para quem é o{' '}
            <span className="italic text-gold-400">Carta do Eu Futuro?</span>
          </h2>
          <p className="font-['Montserrat'] text-sm text-white/40 max-w-2xl mx-auto leading-relaxed font-light tracking-wide">
            Se você se identifica com algum desses perfis, esta experiência foi
            feita especialmente para você.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {perfis.map((perfil, index) => {
            const isVisible = visibleCards.has(index);
            return (
              <div
                key={index}
                data-idx={index}
                ref={(el) => {
                  if (el) cardsRef.current.set(index, el);
                }}
                className={`group relative rounded-xl border border-gold-500/10 bg-white/[0.02] backdrop-blur-sm p-8 hover:border-gold-500/25 hover:bg-white/[0.04] transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gold-500/15 to-gold-600/5 flex items-center justify-center mb-6 group-hover:from-gold-500/25 group-hover:to-gold-600/10 transition-all duration-500">
                  <i
                    className={`${perfil.icon} text-2xl text-gold-500 group-hover:text-gold-400 transition-colors duration-300`}
                  />
                </div>

                {/* Title */}
                <h3 className="font-['Playfair_Display'] text-lg text-white font-semibold mb-3 group-hover:text-gold-300 transition-colors duration-300">
                  {perfil.titulo}
                </h3>

                {/* Description */}
                <p className="font-['Montserrat'] text-xs text-white/40 leading-relaxed font-light tracking-wide mb-5">
                  {perfil.descricao}
                </p>

                {/* Highlight tag */}
                <div className="inline-flex items-center gap-2 bg-gold-500/10 rounded-full px-4 py-1.5">
                  <i className="ri-sparkling-line text-gold-500 text-xs w-3 h-3 flex items-center justify-center" />
                  <span className="font-['Montserrat'] text-[10px] text-gold-400/80 tracking-wider font-medium uppercase">
                    {perfil.destaque}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom message */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-gold-500/10 via-gold-500/5 to-gold-500/10 border border-gold-500/15 rounded-full px-8 py-4">
            <i className="ri-heart-pulse-line text-gold-500 text-lg w-5 h-5 flex items-center justify-center" />
            <span className="font-['Montserrat'] text-xs text-gold-400/80 tracking-wider font-light">
              Não importa onde você está — o que importa é para onde você quer ir
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ParaQuemESection;
